<template>
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Work</div>
                <div class="page-bar-main">Decks</div>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div class="decks">
                <div class="page-filters pf-bar">
                    <div class="pf-search">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></svg>
                        <input type="text" v-model="searchQ" placeholder="Search decks" aria-label="Search decks" @input="search()" />
                    </div>
                    <!-- The person's own folders (deckfolders.ts), then My Decks, plus All Decks for super admins (who get
                         every board from the API) -->
                    <select v-model="boardFilter" aria-label="Which decks" @change="filterChanged()">
                        <option v-for="folder in folders" :key="folder.ID" :value="'folder:' + folder.ID">{{ folder.Name }}</option>
                        <option value="mine">My Decks</option>
                        <option v-if="$store.state.user.superAdmin" value="all">All Decks</option>
                    </select>
                    <!-- Folder buttons sit by the folder dropdown they act on; deck buttons go right -->
                    <button v-if="currentFolder" type="button" class="btn-light" @click="editingFolder = currentFolder">Edit Folder</button>
                    <button v-if="canUseFolders" type="button" class="btn-light" @click="editingFolder = 'new'">New Folder</button>
                    <span class="pf-spacer"></span>
                    <button v-if="$store.state.user.role == 'Admin' || $store.state.user.superAdmin" type="button" class="btn-light" @click="$router.push('/board-templates')">Templates</button>
                    <button type="button" class="btn" @click="showAddBoard = true">Add Deck</button>
                </div>

                <!-- Each employee gets one personal task manager; it isn't listed below, only pinned here. The whole tile opens it
                     (or sets it up). -->
                <template v-if="taskManagerLoaded && (taskManager || canCreateTaskManager)">
                    <router-link v-if="taskManager" class="decks-tm" :to="'/deck/' + taskManager.Slug">
                        <div class="decks-tm-icon"><svg-icon icon="checklist" /></div>
                        <div class="decks-tm-main">
                            <div class="decks-eyebrow">My Task Manager</div>
                            <div class="decks-tm-name">{{ taskManager.Name }}</div>
                            <div v-if="myTaskManager" class="decks-chips">
                                <span v-if="myTaskManager.Overdue" class="decks-chip overdue">{{ myTaskManager.Overdue }} overdue</span>
                                <span v-if="myTaskManager.DueThisWeek" class="decks-chip due">{{ myTaskManager.DueThisWeek }} due this week</span>
                                <span v-if="myTaskManager.InboxCount" class="decks-chip">{{ myTaskManager.InboxCount }} in Inbox</span>
                                <span v-if="!myTaskManager.Overdue && !myTaskManager.DueThisWeek && !myTaskManager.InboxCount" class="decks-chip">Nothing due this week</span>
                            </div>
                        </div>
                        <div v-if="myTaskManager && (myTaskManager.ColumnCounts || []).length" class="decks-tm-cols">
                            <div v-for="col in myTaskManager.ColumnCounts.slice(0, 4)" :key="col.name" class="decks-tm-col">
                                <b>{{ col.count }}</b>
                                <span><i class="decks-dot" :style="{ background: col.color }"></i>{{ col.name }}</span>
                            </div>
                        </div>
                        <span class="decks-arrow" aria-hidden="true">&rsaquo;</span>
                    </router-link>
                    <button v-else type="button" class="decks-tm" :class="settingUpTaskManager ? 'busy' : ''" @click="setUpTaskManager()">
                        <div class="decks-tm-icon"><svg-icon icon="checklist" /></div>
                        <div class="decks-tm-main">
                            <div class="decks-eyebrow">My Task Manager</div>
                            <div class="decks-tm-name">{{ settingUpTaskManager ? 'Setting up...' : 'Set Up Your Task Manager' }}</div>
                            <div class="decks-tm-sub">Your own deck for tasks, including items sent from meetings and your rocks.</div>
                        </div>
                        <span class="decks-arrow" aria-hidden="true">&rsaquo;</span>
                    </button>
                </template>

                <div class="decks-section-h">
                    <h2>{{ currentFolder ? currentFolder.Name : boardFilter == 'all' ? 'All Decks' : 'My Decks' }}</h2>
                    <span>{{ boards.length }} {{ boards.length == 1 ? 'deck' : 'decks' }}</span>
                </div>
                <div v-if="boards.length" class="decks-grid">
                    <router-link v-for="board in boards" :key="board.Slug" class="decks-tile" :to="'/deck/' + board.Slug">
                        <div class="decks-tile-top">
                            <!-- Someone else's task manager says so on its own line, under the name -->
                            <div class="decks-tile-head">
                                <div class="decks-tile-name">{{ board.Name }}</div>
                                <span v-if="board.IsTaskManager" class="tm-badge">Task Manager<template v-if="board.OwnerName"> · {{ board.OwnerName }}</template></span>
                            </div>
                            <span class="decks-arrow" aria-hidden="true">&rsaquo;</span>
                        </div>
                        <!-- Where the deck's cards are: one segment per column, in its color, sized by its card count -->
                        <div class="decks-strip" role="img" :aria-label="stripLabel(board)">
                            <i v-for="col in (board.ColumnCounts || []).filter(c => c.count)" :key="col.name" :style="{ flex: col.count, background: col.color }"></i>
                        </div>
                        <!-- The first LEGEND_MAX columns, one line each; the rest as "+N more" (named on hover) -->
                        <div class="decks-legend">
                            <span v-for="col in (board.ColumnCounts || []).slice(0, LEGEND_MAX)" :key="col.name" :title="col.name + ' ' + col.count">
                                <i class="decks-dot" :style="{ background: col.color }"></i><span class="decks-legend-name">{{ col.name }}</span>{{ col.count }}
                            </span>
                            <span v-if="(board.ColumnCounts || []).length > LEGEND_MAX" class="decks-legend-more"
                                :title="board.ColumnCounts.slice(LEGEND_MAX).map(c => c.name + ' ' + c.count).join('\n')">+{{ board.ColumnCounts.length - LEGEND_MAX }} more</span>
                        </div>
                        <div class="decks-tile-foot">
                            <span class="decks-chip">{{ board.CardCount ? board.CardCount + (board.CardCount == 1 ? ' card' : ' cards') : 'No cards yet' }}</span>
                            <span v-if="board.Overdue" class="decks-chip overdue">{{ board.Overdue }} overdue</span>
                            <span v-if="(board.Members || []).length" class="decks-avatars" :title="board.Members.join(', ')">
                                <span v-for="name in board.Members.slice(0, 3)" :key="name">{{ initials(name) }}</span>
                                <span v-if="board.Members.length > 3" class="more">+{{ board.Members.length - 3 }}</span>
                            </span>
                            <span v-if="board.LastActivity" class="decks-updated" :title="new Date(board.LastActivity).toLocaleString()">{{ relativeTime(board.LastActivity) }}</span>
                        </div>
                    </router-link>
                </div>
                <div v-else-if="loaded" class="decks-empty">
                    <template v-if="searchQ.length >= 3">No decks match that search.</template>
                    <template v-else-if="currentFolder">No decks in this folder yet. <a @click.prevent="editingFolder = currentFolder">Add decks</a></template>
                    <template v-else>No decks yet. Add one to get started.</template>
                </div>
            </div>
        </div>

        <add-company-board v-if="showAddBoard" @close="() => { showAddBoard = false; getBoards(); }" />
        <deck-folder-modal v-if="editingFolder" :boards="folderBoards" :folder="editingFolder == 'new' ? null : editingFolder"
            @close="editingFolder = null" @saved="folderSaved" @deleted="folderDeleted" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import AddCompanyBoard from '@/components/AddCompanyBoard.vue';
import DeckFolderModal from '@/components/DeckFolderModal.vue';
import SvgIcon from '@/components/SvgIcon.vue';

// The Decks page: your task manager pinned as one wide tile, then a tile per deck. Each whole tile opens its deck.
// Favorites, sharing and Delete are in each deck's own page (CompanyBoard.vue). The dropdown narrows the tiles to
// My Decks, All Decks (super admins), or one of the person's folders; the choice is remembered in this browser.
export default defineComponent({
    name: 'CompanyBoards',
    components: {
        AddCompanyBoard,
        DeckFolderModal,
        SvgIcon
    },
    data() {
        return {
            LEGEND_MAX: 10, // columns named on a tile; a deck with more says "+N more"
            allBoards: [] as any,
            boards: [] as any,
            loaded: false,
            searchQ: '',
            boardFilter: '', // 'folder:<ID>', 'mine' or 'all'; set by checkFilter once folders load
            folders: [] as any[],
            editingFolder: null as any, // a folder, 'new', or null when the folder popup is closed
            showAddBoard: false,
            taskManager: null as any,
            canCreateTaskManager: false,
            taskManagerLoaded: false,
            settingUpTaskManager: false
        }
    },
    computed: {
        // Your task manager's row from the decks list, for its numbers (companyboards.ts addBoardStats)
        myTaskManager(): any {
            return this.allBoards.find((b: any) => b.IsMyTaskManager) || null;
        },
        // Folders belong to the person, so need a Team Hub employee record (as a task manager does)
        canUseFolders(): boolean {
            return this.canCreateTaskManager;
        },
        currentFolder(): any {
            return this.folders.find((f: any) => 'folder:' + f.ID == this.boardFilter) || null;
        },
        // What a folder can hold: every deck in the list but your own task manager, which is pinned above it
        folderBoards(): any[] {
            return this.allBoards.filter((board: any) => !board.IsMyTaskManager);
        }
    },
    async created() {
        try {
            this.boardFilter = localStorage.getItem('decksFilter') || '';
        } catch(e) { /* storage blocked: start on the default (checkFilter) */ }
        await Promise.all([ this.getBoards(), this.getTaskManager(), this.getFolders() ]);
        this.checkFilter();

        this.$store.state.routeLoaded = true;
    },
    methods: {
        async getBoards() {
            await axios.get(`${this.$store.state.apiUrl}/api/companyboards`)
                .then(resp => {
                    this.allBoards = resp.data;
                    // Keeps favorited boards in the menu in step with adds / deletes
                    this.$store.state.companyBoards = resp.data;
                    this.search();
                })
                .catch(e => {
                    console.log(e);
                })
            this.loaded = true;
        },
        async getTaskManager() {
            await axios.get(`${this.$store.state.apiUrl}/api/companyboards/taskmanager`)
                .then(resp => {
                    this.taskManager = resp.data.board;
                    this.canCreateTaskManager = resp.data.canCreate;
                })
                .catch(e => {
                    console.log(e);
                })
            this.taskManagerLoaded = true;
        },
        async getFolders() {
            await axios.get(`${this.$store.state.apiUrl}/api/deckfolders`)
                .then(resp => {
                    this.folders = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        // Keeps the remembered choice while it's still there; otherwise (nothing remembered, a deleted folder, or All Decks
        // for a non-super admin) the default: the first folder, or My Decks for someone with none
        checkFilter() {
            if(this.boardFilter == 'mine' || (this.boardFilter == 'all' && this.$store.state.user.superAdmin) || this.currentFolder) {
                this.search();
                return;
            }
            this.boardFilter = this.folders.length ? 'folder:' + this.folders[0].ID : 'mine';
            this.filterChanged();
        },
        filterChanged() {
            try {
                localStorage.setItem('decksFilter', this.boardFilter);
            } catch(e) { /* storage blocked: not remembered */ }
            this.search();
        },
        // Shows the folder just made or edited
        async folderSaved(id: number) {
            this.editingFolder = null;
            await this.getFolders();
            this.boardFilter = 'folder:' + id;
            this.filterChanged();
        },
        async folderDeleted() {
            this.editingFolder = null;
            await this.getFolders();
            this.checkFilter();
        },
        // Creates the user's task manager (the API returns the existing one if it's already there) and opens it
        async setUpTaskManager() {
            if(this.settingUpTaskManager) {
                return;
            }
            this.settingUpTaskManager = true;
            await axios.post(`${this.$store.state.apiUrl}/api/companyboards/taskmanager`)
                .then(resp => {
                    this.$router.push('/deck/' + resp.data.Slug);
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error');
                })
            this.settingUpTaskManager = false;
        },
        // Applies the dropdown (My Decks / All Decks / a folder), then the search text. Your own task manager is
        // pinned above the list instead of in it
        search() {
            let boards = this.allBoards.filter((board: any) => !board.IsMyTaskManager);
            const folder = this.currentFolder;
            if(folder) {
                boards = boards.filter((board: any) => folder.Slugs.includes(board.Slug));
            } else if(this.boardFilter != 'all') {
                boards = boards.filter((board: any) => board.IsMember);
            }
            if(this.searchQ.length >= 3) {
                const searchQLower = this.searchQ.toLowerCase();
                boards = boards.filter((board: any) => {
                    return board.Name.toLowerCase().includes(searchQLower) || (board.OwnerName || '').toLowerCase().includes(searchQLower);
                })
            }
            this.boards = boards;
        },
        initials(name: string): string {
            const parts = String(name || '').trim().split(/\s+/);
            return ((parts[0] || '')[0] || '') + (parts.length > 1 ? (parts[parts.length - 1][0] || '') : '');
        },
        stripLabel(board: any): string {
            const cols = board.ColumnCounts || [];
            return cols.length && board.CardCount ? cols.map((c: any) => `${c.count} ${c.name}`).join(', ') : 'No cards yet';
        },
        // "Just now", "5m ago", "3h ago", "Yesterday", "4d ago", then the date
        relativeTime(value: string): string {
            const then = new Date(value);
            const minutes = Math.floor((Date.now() - then.getTime()) / 60000);
            if(minutes < 1) {
                return 'Just now';
            }
            if(minutes < 60) {
                return `${minutes}m ago`;
            }
            if(minutes < 60 * 24) {
                return `${Math.floor(minutes / 60)}h ago`;
            }
            const days = Math.floor(minutes / (60 * 24));
            if(days == 1) {
                return 'Yesterday';
            }
            if(days < 7) {
                return `${days}d ago`;
            }
            const label = `${this.$store.state.monthShortStrings[then.getMonth() + 1]} ${then.getDate()}`;
            return then.getFullYear() == new Date().getFullYear() ? label : `${label}, ${then.getFullYear()}`;
        }
    }
});
</script>

<style scoped>
.decks { display: grid; gap: 22px; padding-top: 7px; }
/* The filter bar (style.scss .pf-bar) is spaced by the grid gap */
.decks > .pf-bar { margin-bottom: 0; }

/* My Task Manager */
.decks-tm { display: grid; grid-template-columns: auto minmax(0, 1fr) auto auto; gap: 18px; align-items: center; width: 100%; text-align: left; font: inherit; text-decoration: none; color: inherit; background: #fff; border: 1px solid var(--mid-gray); border-radius: 12px; padding: 18px 20px; cursor: pointer; transition: border-color .15s, box-shadow .15s, transform .15s; }
button.decks-tm { grid-template-columns: auto minmax(0, 1fr) auto; }
.decks-tm:hover { border-color: var(--primary-color); box-shadow: 0 6px 18px rgba(44, 42, 41, .08); transform: translateY(-1px); }
.decks-tm.busy { cursor: default; opacity: .7; }
.decks-tm-icon { width: 46px; height: 46px; border-radius: 10px; background: #fbe3e4; display: flex; align-items: center; justify-content: center; }
.decks-tm-icon svg { width: 24px; height: 24px; fill: var(--primary-color); }
.decks-eyebrow { font-family: var(--head-font); font-size: 10.5px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: var(--primary-color); }
.decks-tm-name { font-family: var(--head-font); font-size: 19px; font-weight: 700; margin: 2px 0 6px; color: var(--dark-gray); }
.decks-tm-sub { font-size: 13px; color: var(--font-dark-gray); }
.decks-tm-cols { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(70px, 1fr); gap: 8px; }
.decks-tm-col { background: var(--light-gray); border-radius: 8px; padding: 8px 10px; min-width: 0; }
.decks-tm-col b { display: block; font-family: var(--head-font); font-size: 18px; font-variant-numeric: tabular-nums; color: var(--dark-gray); }
.decks-tm-col span { font-size: 11.5px; color: var(--font-dark-gray); display: flex; align-items: center; gap: 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.decks-chips, .decks-tile-foot { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.decks-chip { font-size: 12px; font-weight: 600; padding: 3px 9px; border-radius: 12px; background: var(--light-gray); color: var(--font-dark-gray); white-space: nowrap; }
.decks-chip.overdue { background: #fbe3e4; color: var(--primary-color); }
.decks-chip.due { background: #FBF0DA; color: #9A6B15; }
.decks-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.decks-arrow { color: var(--font-mid-gray); font-size: 22px; line-height: 1; transition: color .15s, transform .15s; }
.decks-tm:hover .decks-arrow, .decks-tile:hover .decks-arrow { color: var(--primary-color); transform: translateX(2px); }

.decks-section-h { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.decks-section-h h2 { font-family: var(--head-font); font-size: 15px; margin: 0; color: var(--dark-gray); }
.decks-section-h span { color: var(--font-dark-gray); font-size: 12.5px; }

/* Deck tiles */
.decks-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 14px; }
.decks-tile { display: flex; flex-direction: column; gap: 12px; min-width: 0; text-decoration: none; color: inherit; background: #fff; border: 1px solid var(--mid-gray); border-radius: 12px; padding: 16px 16px 14px; transition: border-color .15s, box-shadow .15s, transform .15s; }
.decks-tile:hover { border-color: var(--primary-color); box-shadow: 0 6px 18px rgba(44, 42, 41, .08); transform: translateY(-1px); }
.decks-tile-top { display: flex; align-items: flex-start; gap: 10px; }
.decks-tile-head { flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: flex-start; gap: 6px; }
.decks-tile-name { max-width: 100%; font-family: var(--head-font); font-size: 15px; font-weight: 700; line-height: 1.3; color: var(--dark-gray); word-break: break-word; }
.decks-tile-head .tm-badge { margin-left: 0; max-width: 100%; overflow: hidden; text-overflow: ellipsis; }
.decks-strip { display: flex; gap: 2px; height: 8px; border-radius: 4px; overflow: hidden; background: var(--light-gray); }
.decks-strip i { display: block; height: 100%; }
.decks-legend { display: flex; flex-wrap: wrap; gap: 4px 12px; font-size: 11.5px; color: var(--font-dark-gray); }
.decks-legend > span { display: inline-flex; align-items: center; gap: 5px; max-width: 100%; min-width: 0; white-space: nowrap; font-variant-numeric: tabular-nums; }
.decks-legend-name { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.decks-legend-more { font-weight: 600; color: var(--dark-gray); }
.decks-tile-foot { margin-top: auto; padding-top: 10px; border-top: 1px solid var(--light-gray); }
.decks-avatars { display: flex; }
.decks-avatars span { width: 22px; height: 22px; border-radius: 50%; background: var(--dark-gray); color: #fff; font-family: var(--head-font); font-size: 9px; font-weight: 700; display: flex; align-items: center; justify-content: center; border: 2px solid #fff; margin-left: -6px; text-transform: uppercase; }
.decks-avatars span:first-child { margin-left: 0; }
.decks-avatars span.more { background: var(--mid-gray); color: var(--font-dark-gray); }
.decks-updated { margin-left: auto; font-size: 11.5px; color: var(--font-mid-gray); white-space: nowrap; }
.decks-empty { color: var(--font-dark-gray); font-size: 14px; }
.decks-empty a { color: var(--primary-color); text-decoration: underline; cursor: pointer; }

@media (max-width: 820px) {
    .decks-tm, button.decks-tm { grid-template-columns: auto minmax(0, 1fr); }
    .decks-tm-cols { grid-column: 1 / -1; }
    .decks-tm .decks-arrow { display: none; }
}
@media (prefers-reduced-motion: reduce) {
    .decks-tm, .decks-tile, .decks-arrow { transition: none; }
}
</style>
